import React, { useState } from 'react';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { verifyTimeline } from '@/lib/caseTimeline';
import vectors from '@/lib/caseTimelineVectors.json';

function Checks({ result }) {
  return (
    <div className="space-y-3" data-testid="timeline-result">
      <div className={`rounded-xl border px-4 py-3 text-sm font-semibold ${result.ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-red-500/40 bg-red-500/10 text-red-200'}`} data-testid="timeline-verdict">
        {result.ok ? `All checks pass for ${result.count} events.` : 'One or more checks failed.'}
      </div>
      <ul className="space-y-2">
        {result.checks.map((c) => (
          <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`timeline-check-${c.id}`}>
            {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />}
            <span><span className="text-white">{c.label}.</span> <span className="text-gray-400">{c.detail}</span></span>
          </li>
        ))}
      </ul>
      <div className={`flex items-start gap-2 text-sm ${result.deadline.overdue ? 'text-amber-200' : 'text-gray-400'}`} data-testid="timeline-deadline">
        <Clock className="w-4 h-4 mt-0.5 shrink-0" /> {result.deadline.detail}
      </div>
      <p className="text-xs text-gray-500 break-all" data-testid="timeline-latest-hash">Latest event hash: {result.latestHash}</p>
    </div>
  );
}

export function CaseTimelinePanel() {
  const [text, setText] = useState('');
  const [example, setExample] = useState(null);
  const [result, setResult] = useState(null);
  const load = (t) => { setText(JSON.stringify(t.events, null, 2)); setExample(t); setResult(null); };
  const run = () => {
    let parsed;
    try { parsed = JSON.parse(text); } catch { setResult({ error: 'That is not valid JSON.' }); return; }
    try {
      setResult(verifyTimeline(parsed, example ? vectors.checkedAt : undefined));
    } catch (e) {
      setResult({ error: e.message });
    }
  };
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="timeline-panel">
      <h2 className="text-lg font-semibold mb-1">3. Check a case timeline</h2>
      <p className="text-sm text-gray-400 mb-4">Paste the case events you hold. The checker shows whether anything was removed, reordered, or changed before the latest event, and whether an update is overdue. Event hashing is defined in Draft 0.3.</p>
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="timeline-examples">
        <span className="text-xs text-gray-500 mr-1">Load example:</span>
        {vectors.timelines.map((t) => (
          <button key={t.id} type="button" onClick={() => load(t)} className="rounded-full border border-white/15 px-3 py-1 text-xs text-gray-200 hover:border-amber-400/60 hover:text-white transition-colors" data-testid={`timeline-load-${t.id}`}>{t.id}</button>
        ))}
      </div>
      {example && <p className="text-xs text-gray-400 mb-3" data-testid="timeline-example-note">{example.description.replace(/\.?$/, '.')} Checked as of the test time in the vectors.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <textarea value={text} onChange={(e) => { setText(e.target.value); setExample(null); setResult(null); }} spellCheck={false} rows={14} placeholder='[ { "claimId": "0x...", "caseRef": "...", "eventIndex": 1, ... } ]' className="w-full rounded-xl border border-white/10 bg-[#0a0a0a] p-3 font-mono text-[11px] text-gray-200 focus:border-amber-500/60 focus:outline-none" data-testid="timeline-input" />
          <button type="button" onClick={run} disabled={!text.trim()} className="mt-3 rounded-full bg-white text-black px-6 py-2.5 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-40" data-testid="timeline-check-button">Check in my browser</button>
        </div>
        <div>
          {result?.error && <p className="text-sm text-red-300" data-testid="timeline-error">{result.error}</p>}
          {result && !result.error && <Checks result={result} />}
          <p className="text-xs text-gray-500 mt-4">A chain check covers only the copy you hold. It cannot show who wrote the events or catch a change to the latest event. The merchant's signature is what does that.</p>
        </div>
      </div>
    </section>
  );
}
